import { Component } from '@theme/component';
import { debounce, prefersReducedMotion, onDocumentLoaded, onAnimationEnd } from '@theme/utilities';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * Custom search overlay component using Shopify Ajax Predictive Search API.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} searchResults - The search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} liveRegion - Screen reader live region.
 * @property {HTMLElement} closeModalButton - Close modal button (mobile).
 * @property {HTMLFormElement} form - The search form.
 * @property {HTMLElement[]} [resultsItems] - Navigable result items.
 * @extends {Component<Refs>}
 */
class CustomSearchOverlay extends Component {
  requiredRefs = ['searchInput', 'searchResults', 'resetButton'];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string | null} Cached default state HTML */
  #defaultStateHTML = null;

  /** @type {boolean} Track keyboard vs mouse navigation */
  #isKeyboardNavigation = false;

  /** @type {MediaQueryList} Desktop breakpoint */
  #desktopMQ = window.matchMedia('(min-width: 750px)');

  /** @type {HTMLElement | null} */
  #headerForm = null;
  /** @type {HTMLInputElement | null} */
  #headerInput = null;
  /** @type {HTMLElement | null} */
  #headerClose = null;
  /** @type {HTMLElement | null} */
  #headerClear = null;
  /** @type {HTMLElement | null} */
  #searchIconButton = null;
  /** @type {AbortController | null} */
  #desktopSearchController = null;
  /** @type {number | null} */
  #desktopScrollY = null;

  get #isDesktop() {
    return this.#desktopMQ.matches;
  }

  /**
   * Get the currently active search input (header on desktop, overlay on mobile).
   * @returns {HTMLInputElement}
   */
  get #activeInput() {
    return (this.#isDesktop && this.#headerInput) ? this.#headerInput : this.refs.searchInput;
  }

  /**
   * Get the dialog component ancestor.
   * @returns {DialogComponent | null}
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  /**
   * Get the currency code from data attribute.
   * @returns {string}
   */
  get #currencyCode() {
    return this.dataset.currency || 'GBP';
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    // Cache the default state HTML on first render
    this.#cacheDefaultState();

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      // Override showDialog to use non-modal on desktop
      const originalShowDialog = dialog.showDialog.bind(dialog);
      dialog.showDialog = () => {
        if (this.#isDesktop && this.#searchIconButton) {
          this.#openDesktopSearch();
        } else {
          originalShowDialog();
        }
      };

      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false);
      this.#setupHeaderSearch();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#desktopSearchController?.abort();
  }

  // ---------------------------------------------------------------------------
  // Default state caching
  // ---------------------------------------------------------------------------

  #cacheDefaultState() {
    const defaultEl = this.refs.searchResults.querySelector('[data-default-state]');
    if (defaultEl) {
      this.#defaultStateHTML = defaultEl.outerHTML;
    }
  }

  // ---------------------------------------------------------------------------
  // Dialog integration
  // ---------------------------------------------------------------------------

  /**
   * Handle CMD+K keyboard shortcut.
   * @param {KeyboardEvent} event
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      if (this.#isDesktop) {
        const dialogEl = this.dialog?.refs?.dialog;
        if (dialogEl?.open) {
          this.#closeDesktopSearch();
        } else {
          this.#openDesktopSearch();
        }
      } else {
        this.dialog?.toggleDialog();
      }
    }
  };

  /**
   * Handle dialog open - focus the active search input.
   */
  #handleDialogOpen = () => {
    requestAnimationFrame(() => {
      this.#activeInput?.focus();
    });
  };

  /**
   * Handle dialog close - reset search state and clean up header form.
   */
  #handleDialogClose = () => {
    this.#resetSearch();

    // Clean up header form if it was open (desktop)
    if (this.#headerForm && !this.#headerForm.hidden) {
      if (this.#headerInput) this.#headerInput.value = '';
      if (this.#headerClear) this.#headerClear.hidden = true;
      this.#headerForm.hidden = true;
      if (this.#searchIconButton) this.#searchIconButton.hidden = false;
    }

    // Clean up backdrop and desktop listeners
    const backdrop = this.dialog?.querySelector('[data-search-backdrop]');
    if (backdrop) backdrop.classList.remove('is-visible');
    this.#desktopSearchController?.abort();
    this.#desktopSearchController = null;
  };

  /**
   * Maintain focus on input when clicking non-interactive areas.
   * @param {MouseEvent} event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractive =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractive) {
      this.#activeInput?.focus();
    }
  };

  /**
   * Handle clicks inside the body area (delegate for results items).
   * @param {MouseEvent} _event
   */
  handleBodyClick(_event) {
    // Focus management handled by #handleModalClick
  }

  // ---------------------------------------------------------------------------
  // Desktop header search
  // ---------------------------------------------------------------------------

  /**
   * Set up the header search input for desktop.
   * Intercepts the search icon click to open the dialog non-modally
   * and bridges the header input to the search logic.
   */
  #setupHeaderSearch() {
    // Find the desktop search-button (not the mobile duplicate with desktop:hidden)
    let desktopContainer = null;
    for (const el of document.querySelectorAll('search-button.search-action')) {
      if (!el.classList.contains('desktop:hidden')) {
        desktopContainer = el;
        break;
      }
    }
    if (!desktopContainer) return;

    this.#searchIconButton = desktopContainer.querySelector('[data-search-icon-button]');
    this.#headerForm = desktopContainer.querySelector('[data-search-header-form]');
    this.#headerInput = desktopContainer.querySelector('[data-search-header-input]');
    this.#headerClose = desktopContainer.querySelector('[data-search-header-close]');
    this.#headerClear = desktopContainer.querySelector('[data-search-header-clear]');

    if (!this.#searchIconButton || !this.#headerForm || !this.#headerInput) return;

    const { signal } = this.#controller;

    // Header input events
    this.#headerInput.addEventListener('input', this.#handleHeaderInput, { signal });
    this.#headerInput.addEventListener('keydown', this.#handleHeaderKeyDown, { signal });

    // Header close button
    this.#headerClose?.addEventListener('click', this.#closeDesktopSearch, { signal });

    // Header clear button
    this.#headerClear?.addEventListener('click', this.#clearHeaderInput, { signal });
  }

  /**
   * Open the search on desktop: show header form, open dialog non-modally.
   */
  #openDesktopSearch() {
    if (!this.#searchIconButton || !this.#headerForm || !this.#headerInput) return;

    // Hide search icon, show header form
    this.#searchIconButton.hidden = true;
    this.#headerForm.hidden = false;

    // Open dialog non-modally
    const dialogEl = this.dialog?.refs?.dialog;
    if (dialogEl && !dialogEl.open) {
      dialogEl.show();
      this.#applyDesktopScrollLock();
      this.dialog?.dispatchEvent(new DialogOpenEvent());

      // Show backdrop overlay and focus input
      const backdrop = this.dialog?.querySelector('[data-search-backdrop]');
      if (backdrop) backdrop.classList.add('is-visible');

      requestAnimationFrame(() => {
        this.#headerInput?.focus();
      });
    }

    // Set up desktop-specific listeners (outside click, escape)
    this.#desktopSearchController = new AbortController();
    const { signal } = this.#desktopSearchController;

    const backdrop = this.dialog?.querySelector('[data-search-backdrop]');

    setTimeout(() => {
      document.addEventListener('click', this.#handleDesktopOutsideClick, { signal });
      document.addEventListener('keydown', this.#handleDesktopEscape, { signal });
      if (backdrop) backdrop.addEventListener('click', this.#closeDesktopSearch, { signal });
    });
  }

  /**
   * Close the desktop search: hide header form, close dialog.
   */
  #closeDesktopSearch = async () => {
    const dialogEl = this.dialog?.refs?.dialog;
    if (!dialogEl?.open || dialogEl.classList.contains('dialog-closing')) return;

    // Immediately abort desktop listeners to prevent re-entry
    this.#desktopSearchController?.abort();
    this.#desktopSearchController = null;

    // Hide backdrop overlay
    const backdrop = this.dialog?.querySelector('[data-search-backdrop]');
    if (backdrop) backdrop.classList.remove('is-visible');

    // Close animation
    dialogEl.classList.add('dialog-closing');
    await onAnimationEnd(dialogEl, undefined, { subtree: false });

    // Release scroll lock
    this.#releaseDesktopScrollLock();

    // Close the dialog
    dialogEl.close();
    dialogEl.classList.remove('dialog-closing');

    // Reset header form
    if (this.#headerInput) this.#headerInput.value = '';
    if (this.#headerClear) this.#headerClear.hidden = true;
    if (this.#headerForm) this.#headerForm.hidden = true;
    if (this.#searchIconButton) this.#searchIconButton.hidden = false;

    // Dispatch close event (triggers #handleDialogClose → #resetSearch)
    this.dialog?.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Handle header input changes (debounced).
   */
  #handleHeaderInput = debounce(() => {
    const term = this.#headerInput?.value.trim() || '';
    this.#currentIndex = -1;

    if (this.#headerClear) {
      this.#headerClear.hidden = term.length === 0;
    }

    if (!term.length) {
      this.#showDefaultState();
      return;
    }

    this.#fetchSearchResults(term);
  }, 200);

  /**
   * Handle keyboard navigation from header input.
   * @param {KeyboardEvent} event
   */
  #handleHeaderKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#closeDesktopSearch();
      return;
    }
    this.onSearchKeyDown(event);
  };

  /**
   * Clear the header search input.
   */
  #clearHeaderInput = () => {
    if (this.#headerInput) {
      this.#headerInput.value = '';
      this.#headerInput.focus();
    }
    if (this.#headerClear) this.#headerClear.hidden = true;
    this.#showDefaultState();
  };

  /**
   * Close desktop search when clicking outside the results or header form.
   * @param {MouseEvent} event
   */
  #handleDesktopOutsideClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Don't close if clicking inside the results body
    const body = this.querySelector('.custom-search-overlay__body');
    if (body?.contains(target)) return;

    // Don't close if clicking inside the header form
    if (this.#headerForm?.contains(target)) return;

    this.#closeDesktopSearch();
  };

  /**
   * Close desktop search on Escape key.
   * @param {KeyboardEvent} event
   */
  #handleDesktopEscape = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeDesktopSearch();
    }
  };

  /**
   * Apply scroll lock for desktop non-modal dialog.
   */
  #applyDesktopScrollLock() {
    this.#desktopScrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#desktopScrollY}px`;
  }

  /**
   * Release scroll lock for desktop non-modal dialog.
   */
  #releaseDesktopScrollLock() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    if (this.#desktopScrollY !== null) {
      window.scrollTo({ top: this.#desktopScrollY, behavior: 'instant' });
      this.#desktopScrollY = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Search logic
  // ---------------------------------------------------------------------------

  /**
   * Debounced search handler (200ms). Fires on input change.
   */
  search = debounce((event) => {
    if (!event.inputType) return;

    const term = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!term.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#fetchSearchResults(term);
  }, 200);

  /**
   * Fetch search results from Shopify Ajax Predictive Search API.
   * @param {string} term - The search term.
   */
  async #fetchSearchResults(term) {
    const abortController = this.#createAbortController();

    try {
      const params = new URLSearchParams({
        'q': term,
        'resources[type]': 'product,query,collection',
        'resources[limit]': '4',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor',
      });

      const response = await fetch(
        `${this.dataset.searchUrl || '/search'}/suggest.json?${params}`,
        { signal: abortController.signal }
      );

      if (abortController.signal.aborted) return;

      if (!response.ok) {
        throw new Error(`Predictive search error: ${response.status}`);
      }

      const json = await response.json();

      if (abortController.signal.aborted) return;

      const results = json.resources?.results;
      if (!results) return;

      const hasProducts = results.products && results.products.length > 0;
      const hasQueries = results.queries && results.queries.length > 0;
      const hasCollections = results.collections && results.collections.length > 0;

      if (!hasProducts && !hasQueries && !hasCollections) {
        this.#showNoResultsState(term);
      } else {
        this.#showResultsState(results, term);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Search error:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // State rendering
  // ---------------------------------------------------------------------------

  /**
   * Show the default state (cached server-rendered HTML).
   */
  #showDefaultState() {
    if (!this.#defaultStateHTML) return;

    const { searchResults } = this.refs;

    // Remove any existing results/no-results
    const existingResults = searchResults.querySelector('[data-search-results]');
    const existingNoResults = searchResults.querySelector('[data-no-results]');
    if (existingResults) existingResults.remove();
    if (existingNoResults) existingNoResults.remove();

    // Restore default state if not already present
    if (!searchResults.querySelector('[data-default-state]')) {
      const temp = document.createElement('div');
      temp.innerHTML = this.#defaultStateHTML;
      const defaultEl = temp.firstElementChild;
      if (defaultEl) {
        searchResults.prepend(defaultEl);
      }
    } else {
      const defaultEl = searchResults.querySelector('[data-default-state]');
      if (defaultEl) defaultEl.removeAttribute('hidden');
    }

    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.#announceResults('');
  }

  /**
   * Render search results from Ajax Predictive Search API response.
   * @param {object} data - The resources.results response data.
   * @param {string} term - The search term.
   */
  #showResultsState(data, term) {
    const { searchResults } = this.refs;

    // Hide default state
    const defaultEl = searchResults.querySelector('[data-default-state]');
    if (defaultEl) defaultEl.setAttribute('hidden', '');

    // Remove previous results
    const existingResults = searchResults.querySelector('[data-search-results]');
    const existingNoResults = searchResults.querySelector('[data-no-results]');
    if (existingResults) existingResults.remove();
    if (existingNoResults) existingNoResults.remove();

    // Clone the results template
    const template = searchResults.querySelector('[data-search-results-template]');
    if (!template) return;

    const clone = /** @type {HTMLTemplateElement} */ (template).content.cloneNode(true);
    const resultsEl = /** @type {DocumentFragment} */ (clone).querySelector('[data-search-results]');
    if (!resultsEl) return;

    // Render queries
    const queryList = resultsEl.querySelector('[data-query-list]');
    const queryTemplate = searchResults.querySelector('[data-search-query-template]');
    if (queryList && queryTemplate && data.queries?.length > 0) {
      for (const query of data.queries) {
        const queryClone = /** @type {HTMLTemplateElement} */ (queryTemplate).content.cloneNode(true);
        const link = /** @type {DocumentFragment} */ (queryClone).querySelector('[data-query-link]');
        const text = /** @type {DocumentFragment} */ (queryClone).querySelector('[data-query-text]');
        if (link && text) {
          link.href = query.url || `${this.dataset.searchUrl}?q=${encodeURIComponent(query.text)}`;
          text.innerHTML = (query.styled_text || query.text).replace(/<\/?mark>/gi, (m) => m.startsWith('</') ? '</strong>' : '<strong>');
        }
        queryList.appendChild(queryClone);
      }
    } else {
      const queriesSection = resultsEl.querySelector('[data-results-queries]');
      if (queriesSection) queriesSection.hidden = true;
    }

    // Render collections
    const collectionList = resultsEl.querySelector('[data-collection-list]');
    const collectionTemplate = searchResults.querySelector('[data-search-collection-template]');
    const collectionsSection = resultsEl.querySelector('[data-results-collections]');
    if (collectionList && collectionTemplate && data.collections?.length > 0 && collectionsSection) {
      collectionsSection.hidden = false;
      for (const collection of data.collections.slice(0, 2)) {
        const colClone = /** @type {HTMLTemplateElement} */ (collectionTemplate).content.cloneNode(true);
        const link = /** @type {DocumentFragment} */ (colClone).querySelector('[data-collection-link]');
        const text = /** @type {DocumentFragment} */ (colClone).querySelector('[data-collection-text]');
        if (link && text) {
          link.href = collection.url || `/collections/${collection.handle}`;
          text.innerHTML = this.#boldSearchTerm(this.#toTitleCase(collection.title), term);
        }
        collectionList.appendChild(colClone);
      }
    }

    // Render products
    const productsGrid = resultsEl.querySelector('[data-products-grid]');
    const productTemplate = searchResults.querySelector('[data-search-product-template]');
    if (productsGrid && productTemplate && data.products?.length > 0) {
      for (const product of data.products) {
        const prodClone = /** @type {HTMLTemplateElement} */ (productTemplate).content.cloneNode(true);
        const link = /** @type {DocumentFragment} */ (prodClone).querySelector('[data-product-link]');
        const image = /** @type {DocumentFragment} */ (prodClone).querySelector('[data-product-image]');
        const badge = /** @type {DocumentFragment} */ (prodClone).querySelector('[data-product-badge]');
        const title = /** @type {DocumentFragment} */ (prodClone).querySelector('[data-product-title]');
        const price = /** @type {DocumentFragment} */ (prodClone).querySelector('[data-product-price]');

        if (link) link.href = product.url || `/products/${product.handle}`;

        // Show sale badge on image if product is on sale
        const minPrice = parseFloat(product.price || '0');
        const comparePrice = parseFloat(product.compare_at_price_max || '0');
        if (badge && comparePrice > 0 && comparePrice > minPrice) {
          badge.hidden = false;
        }

        if (image && product.featured_image?.url) {
          image.src = product.featured_image.url + '&width=264';
          image.alt = product.featured_image.alt || product.title;
        } else if (image && product.image) {
          image.src = product.image + '&width=264';
          image.alt = product.title;
        } else if (image) {
          image.closest('.search-product-card__media')?.classList.add('search-product-card__placeholder');
          image.remove();
        }

        if (title) title.textContent = product.title;

        if (price) {
          price.innerHTML = this.#formatProductPrice(product);
        }

        productsGrid.appendChild(prodClone);

        // Fetch health benefits from metafield via Section Rendering API
        const handle = product.handle || product.url?.split('/products/')[1]?.split('?')[0];
        if (handle) {
          const benefitsEl = productsGrid.lastElementChild?.querySelector('[data-product-benefits]');
          if (benefitsEl) {
            this.#fetchProductBenefits(handle, benefitsEl);
          }
        }
      }
    }

    searchResults.appendChild(resultsEl);

    // Show and wire up the "View All" buttons inside the results template
    const searchUrl = `${this.dataset.searchUrl}?q=${encodeURIComponent(term)}`;
    const viewAllWrapper = searchResults.querySelector('[data-view-all-wrapper]');
    const viewAllButton = searchResults.querySelector('[data-view-all-button]');
    const viewAllInline = searchResults.querySelector('[data-view-all-inline]');
    if (viewAllWrapper) viewAllWrapper.hidden = false;
    if (viewAllButton) {
      viewAllButton.onclick = (e) => {
        e.preventDefault();
        window.location.href = searchUrl;
      };
    }
    if (viewAllInline) {
      viewAllInline.hidden = false;
      viewAllInline.href = searchUrl;
    }

    this.refs.searchInput.setAttribute('aria-expanded', 'true');

    // Announce results
    const count = (data.products?.length || 0) + (data.queries?.length || 0) + (data.collections?.length || 0);
    this.#announceResults(`${count} search results available`);

    this.#resetScrollPositions();
  }

  /**
   * Show the no-results state.
   * @param {string} term - The search term that returned no results.
   */
  #showNoResultsState(term) {
    const { searchResults } = this.refs;

    // Hide default state
    const defaultEl = searchResults.querySelector('[data-default-state]');
    if (defaultEl) defaultEl.setAttribute('hidden', '');

    // Remove previous results
    const existingResults = searchResults.querySelector('[data-search-results]');
    const existingNoResults = searchResults.querySelector('[data-no-results]');
    if (existingResults) existingResults.remove();
    if (existingNoResults) existingNoResults.remove();

    // Clone the no-results template
    const template = searchResults.querySelector('[data-no-results-template]');
    if (!template) return;

    const clone = /** @type {HTMLTemplateElement} */ (template).content.cloneNode(true);
    const noResultsEl = /** @type {DocumentFragment} */ (clone).querySelector('[data-no-results]');

    if (noResultsEl) {
      const message = noResultsEl.querySelector('[data-no-results-message]');
      if (message) {
        message.textContent = `No results for "${term}". Please check your spelling or try a different search.`;
      }
      searchResults.appendChild(noResultsEl);
    }

    this.refs.searchInput.setAttribute('aria-expanded', 'true');
    this.#announceResults(`No results found for ${term}`);
  }

  // ---------------------------------------------------------------------------
  // Price formatting
  // ---------------------------------------------------------------------------

  /**
   * Format product price HTML from Ajax Predictive Search API data.
   * The API returns prices as decimal strings (e.g., "25.00").
   * @param {object} product - The product from Ajax API response.
   * @returns {string} HTML string for the price display.
   */
  #formatProductPrice(product) {
    const minPrice = parseFloat(product.price || '0');
    const comparePrice = parseFloat(product.compare_at_price_max || '0');
    const currency = this.#currencyCode;

    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    });

    if (comparePrice > 0 && comparePrice > minPrice) {
      return `
        <span class="search-product-card__compare-price">${formatter.format(comparePrice)}</span>
        <span class="search-product-card__sale-price">${formatter.format(minPrice)}</span>
      `;
    }

    return `<span class="search-product-card__regular-price">${formatter.format(minPrice)}</span>`;
  }

  // ---------------------------------------------------------------------------
  // Product benefits (metafields via Section Rendering API)
  // ---------------------------------------------------------------------------

  /**
   * Fetch health benefits for a product via the Section Rendering API.
   * Uses the product-search-meta section to access metafields server-side.
   * @param {string} handle - The product handle.
   * @param {HTMLElement} benefitsEl - The benefits list element to populate.
   */
  async #fetchProductBenefits(handle, benefitsEl) {
    try {
      const response = await fetch(
        `/products/${handle}?section_id=product-search-meta`,
        { signal: this.#activeFetch?.signal }
      );

      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const metaEl = doc.querySelector('[data-product-search-meta]');
      if (!metaEl) return;

      const value = JSON.parse(metaEl.textContent || 'null');
      if (!Array.isArray(value) || value.length === 0) return;

      for (const benefit of value.slice(0, 2)) {
        const li = document.createElement('li');
        li.className = 'search-product-card__benefit';
        li.innerHTML = `
          <svg class="search-product-card__tick" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>${this.#escapeHTML(benefit)}</span>
        `;
        benefitsEl.appendChild(li);
      }
    } catch {
      // Silently fail — benefits are non-critical
    }
  }

  // ---------------------------------------------------------------------------
  // Keyboard navigation
  // ---------------------------------------------------------------------------

  /**
   * Get all navigable result items.
   * @returns {HTMLElement[]}
   */
  get #allResultsItems() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[ref="resultsItems[]"]'))
    );
  }

  /**
   * Get the current selected index.
   * @returns {number}
   */
  get #currentIndex() {
    return this.#allResultsItems?.findIndex(
      (item) => item.getAttribute('aria-selected') === 'true'
    ) ?? -1;
  }

  /**
   * Set the current selected index and manage aria-selected.
   * @param {number} index
   */
  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      const parent = item.closest('[role="option"]') || item;
      if (itemIndex === index) {
        parent.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          parent.classList.add('keyboard-focus');
        }
        parent.scrollIntoView({
          behavior: prefersReducedMotion() ? 'instant' : 'smooth',
          block: 'nearest',
        });
      } else {
        parent.removeAttribute('aria-selected');
      }
    }
    this.#activeInput?.focus();
  }

  /**
   * Get the currently selected item.
   * @returns {HTMLElement | undefined}
   */
  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Handle keyboard navigation within search results.
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          const item = this.#currentItem;
          // The item might be the <a> itself or a parent with an <a> child
          if (item instanceof HTMLAnchorElement) {
            item.click();
          } else {
            item?.querySelector('a')?.click();
          }
        } else {
          const searchUrl = new URL(this.dataset.searchUrl || '/search', location.origin);
          searchUrl.searchParams.set('q', this.#activeInput?.value || '');
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Reset / UI helpers
  // ---------------------------------------------------------------------------

  /**
   * Public reset method (debounced). Used by on:click="/resetSearch".
   * @param {boolean} [keepFocus=true]
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.#activeInput?.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Internal reset: clear input, hide results, restore default state.
   */
  #resetSearch = () => {
    this.#currentIndex = -1;
    this.refs.searchInput.value = '';
    this.#hideResetButton();
    this.#showDefaultState();
  };

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  /**
   * Create a new AbortController, aborting any previous in-flight fetch.
   * @returns {AbortController}
   */
  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Reset scroll positions for the results container.
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const body = this.querySelector('.custom-search-overlay__body');
      if (body instanceof HTMLElement) {
        body.scrollTop = 0;
      }
    });
  }

  /**
   * Announce results to screen readers via live region.
   * @param {string} message
   */
  #announceResults(message) {
    const { liveRegion } = this.refs;
    if (liveRegion) {
      liveRegion.textContent = message;
    }
  }

  /**
   * Bold occurrences of the search term within a string (HTML-safe).
   * @param {string} str - The text to process.
   * @param {string} term - The search term to bold.
   * @returns {string} HTML string with matched portions wrapped in <strong>.
   */
  /**
   * Convert a string to title case (e.g., "CBD OIL" → "Cbd Oil").
   * @param {string} str
   * @returns {string}
   */
  #toTitleCase(str) {
    return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }

  #boldSearchTerm(str, term) {
    const escaped = this.#escapeHTML(str);
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return escaped.replace(new RegExp(`(${escapedTerm})`, 'gi'), '<strong>$1</strong>');
  }

  /**
   * Escape HTML to prevent XSS when rendering user-generated content.
   * @param {string} str
   * @returns {string}
   */
  #escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('custom-search-overlay')) {
  customElements.define('custom-search-overlay', CustomSearchOverlay);
}
